import { DocumentNode, gql } from '@apollo/client';
import {
  Box,
  Button,
  Checkbox,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { Redraft } from '@/components/Redraft';
import { logError } from '@/imports/logging/ClientLogger';
import { Prose } from '@/imports/ui/chakra/components/Prose';

import { PrivacyPolicyStep_TenantFragment } from './graphql-types/PrivacyPolicyStep_TenantFragment';

import { ActivationModalBody } from '../ActivationModalBody';
import { ActivationModalFooter } from '../ActivationModalFooter';
import { ActivationModalHeader } from '../ActivationModalHeader';
import { useAcceptPrivacyPolicy } from './useAcceptPrivacyPolicy';

const TENANT_FRAGMENT = gql`
  fragment PrivacyPolicyStep_TenantFragment on Tenant {
    _id
    privacyPolicy
  }
`;

export const PrivacyPolicyStep: React.FC<{
  tenant?: PrivacyPolicyStep_TenantFragment;
}> & {
  tenantFragment: DocumentNode;
} = (props) => {
  const toast = useToast();
  const [accepted, setAccepted] = React.useState(false);
  const [submitAcceptPrivacyPolicy, submitAcceptPrivacyPolicyResult] =
    useAcceptPrivacyPolicy(true);

  const checkboxText = useBreakpointValue({
    base: 'I read & accept this code',
    md: 'I read & accept this privacy policy',
  });

  const { handleSubmit } = useForm();

  if (!props.tenant) {
    return null;
  }

  return (
    <Box
      as="form"
      display="flex"
      flexDirection="column"
      overflow="auto"
      flexGrow="1"
      noValidate
      onSubmit={handleSubmit(async () => {
        try {
          await submitAcceptPrivacyPolicy();
        } catch (err) {
          logError(err);
          toast({
            title:
              'Failed to accept the privacy policy, please refresh and try again.',
            status: 'error',
          });
        }
      })}
    >
      <ActivationModalHeader
        title="Please read & accept our privacy policy."
        subtitle="The privacy policy ensures that we cultivate a community we all enjoy and find useful!"
      />
      <ActivationModalBody>
        <Box bg="gray.50" pt="1" pr="4" pl="4" pb="4" borderRadius="base">
          {props.tenant.privacyPolicy && (
            <Prose>
              <Redraft rawDraftContentState={props.tenant.privacyPolicy} />
            </Prose>
          )}
        </Box>
      </ActivationModalBody>
      <ActivationModalFooter>
        <Checkbox
          isChecked={accepted}
          onChange={() => setAccepted((oldValue) => !oldValue)}
        >
          {checkboxText}
        </Checkbox>
        <Button
          type="submit"
          isDisabled={!accepted}
          isLoading={submitAcceptPrivacyPolicyResult.loading}
        >
          Accept
        </Button>
      </ActivationModalFooter>
    </Box>
  );
};

PrivacyPolicyStep.tenantFragment = TENANT_FRAGMENT;
